import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { CaraWorkspaceFileInput } from '~/api/graphql';

/**
 * One collapsed group holding a form's optional upload buckets.
 *
 * The employee benefits presets each carry three or four buckets that change
 * what a run can conclude without deciding whether it runs at all, and each
 * declares itself to the prompt on its own line: a bucket left empty switches
 * off a block of the analysis and gets named as the input that would switch it
 * back on. Collecting them into one bucket would be cheaper on screen at the
 * price of those declarations, so they keep their own buckets and give up their
 * place on it.
 *
 * Staged work is the one thing a collapse must not hide. The panel stays mounted
 * so a file survives being closed over, the button carries the count so an
 * upload reads as having landed without reopening anything, and a group that
 * already holds files starts open.
 */
export const PresetOptionalUploads = (props: {
  /** What is inside, in the broker's words rather than the schema's. */
  label: string;
  /** Every file staged in the buckets below, for the count and the initial state. */
  files: CaraWorkspaceFileInput[];
  children: React.ReactNode;
}) => (
  <Headless.Disclosure as="div" defaultOpen={props.files.length > 0}>
    {({ open }) => (
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
        <Headless.DisclosureButton className="flex w-full flex-row items-center gap-2 rounded-lg px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-white/5 transition-all duration-150">
          <ChevronDownIcon
            className={clsx(
              'size-4 shrink-0 text-neutral-500 dark:text-neutral-400 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
          <span className="text-sm font-medium text-neutral-900 dark:text-white">
            {props.label} (optional)
          </span>
          {props.files.length > 0 && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              · {props.files.length === 1 ? '1 file' : `${props.files.length} files`}
            </span>
          )}
        </Headless.DisclosureButton>
        <Headless.DisclosurePanel
          unmount={false}
          className="flex flex-col gap-6 border-t border-neutral-200 dark:border-neutral-700 px-3 py-4"
        >
          {props.children}
        </Headless.DisclosurePanel>
      </div>
    )}
  </Headless.Disclosure>
);
